import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TailSpin } from 'react-loader-spinner';
import { CellProps, ColumnGroup } from 'react-table';

import { RegisterType } from 'types';
import { BudgetProductType } from 'types/api/budgets.type';

import { QuantityCell } from './QuantityCell';

type SubProductRow = {
  id: string;
  itemCode: string;
  mainFabricCode: string;
  itemColorCode: string;
  mainFabricDesc: string;
  styleCodeAndStyleDesc: string;
  prevSold: number;
  currSold: number;
  currQuantity: number;
};

export const useColumns = (
  updateQuantity: (productId: string, quantity: string) => void,
  budgetStatus: string,
  date: string,
  product: BudgetProductType | undefined,
  register?: RegisterType,
  isEndDatePassed?: boolean,
  isUpdating?: boolean
) => {
  const { t } = useTranslation();

  // Keep updateQuantity stable to prevent column re-creation
  const updateQuantityRef = useRef(updateQuantity);
  updateQuantityRef.current = updateQuantity;

  // Memoize the disabled state calculation
  const isDisabled = useMemo(
    () => budgetStatus?.toLocaleLowerCase() === 'sent' || Boolean(isEndDatePassed),
    [budgetStatus, isEndDatePassed]
  );

  // Stable callback reference to prevent unnecessary re-renders
  const handleQuantityChange = useCallback((rowId: string, quantity: string) => {
    updateQuantityRef.current(rowId, quantity);
  }, []);

  // Memoize static columns
  const staticColumns = useMemo(
    () => [
      {
        Header: t('common.code')!,
        accessor: 'itemCode',
      },
      {
        Header: t('catalogue.fabric')!,
        accessor: 'mainFabricCode',
      },
      {
        Header: t('catalogue.color')!,
        accessor: 'itemColorCode',
      },
      {
        Header: t('catalogue.material')!,
        accessor: 'mainFabricDesc',
      },
      {
        Header: t('statistics.marketing')!,
        accessor: 'styleCodeAndStyleDesc',
      },
      {
        Header: t('budget.prevSold')!,
        accessor: 'prevSold',
      },
      {
        Header: t('product.sold')!,
        accessor: 'currSold',
      },
    ],
    [t]
  );

  // Memoize quantity column separately - it does NOT depend on isUpdating
  const quantityColumn = useMemo(
    () => ({
      Header: `Full ${date}`,
      accessor: 'currQuantity',
      Cell: ({ value, row }: CellProps<SubProductRow, number>) => {
        const rowId = row.original.id;
        const initialValue = Number(value) || 0;

        return (
          <QuantityCell
            rowId={rowId}
            initialValue={initialValue}
            isDisabled={isDisabled}
            onQuantityChange={handleQuantityChange}
          />
        );
      },
    }),
    [date, isDisabled, handleQuantityChange]
  );

  // Memoize forecast column separately - it DOES depend on isUpdating
  const forecastColumn = useMemo(
    () => ({
      Header: t('statistics.forecast')!,
      accessor: 'forecast',
      Cell: ({ row }: CellProps<SubProductRow, any>) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{row.original.currSold + row.original.currQuantity}</span>
          {isUpdating && <TailSpin height="15" width="15" color="#286090" />}
        </div>
      ),
    }),
    [t, isUpdating]
  );

  return useMemo(
    (): ColumnGroup[] => [
      {
        Header: 'Budgets',
        columns: [...staticColumns, quantityColumn, forecastColumn],
      },
    ],
    [staticColumns, quantityColumn, forecastColumn]
  );
};
